


import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Typography,
    Button,
    FormControl,
    CircularProgress, TextField, Box, IconButton, Dialog, DialogContent, InputAdornment
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logo from '../../styles/logo.png';

function ResetPasswordForm() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [resetSuccess, setResetSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const forgotPasswordApiUrl = process.env.REACT_APP_FORGOT_PASSWORD_API;
    const resetpasswordApiUrl = process.env.REACT_APP_RESET_PASSWORD_API;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [step, setStep] = useState(1);

    const handleSendOTP = async () => {
        try {
            const response = await axios.post(forgotPasswordApiUrl, { email });
            if (response.data.success) {
                setOtpSent(true);
                setStep(2);
                // The backend emails the OTP itself; it is never sent to the browser.
                setResponseMessage("OTP sent successfully to your email.");
                setDialogOpen(true);
            } else {
                setErrorMessage("Unable to send OTP.");
                setDialogOpen(true);
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "Please enter valid Data...");
            setDialogOpen(true);
        }
    };

    const handleVerifyOTP = () => {
        setStep(3);
        setTimeout(() => {
            setOtpVerified(true);
        }, 1000);
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        if (newPassword !== confirmPassword) {
            setResponseMessage("Passwords do not match");
            setDialogOpen(true);
            return;
        }
        try {
            const response = await axios.post(resetpasswordApiUrl, { email, otp, newPassword });
            if (response.data.success) {
                // The backend sends the confirmation email.
                setResetSuccess(true);
            } else {
                setErrorMessage("Unable to reset password.");
                setDialogOpen(true);
            }
        } catch (error) {
            // Expired OTP / too many attempts / wrong OTP come back as 400 with a message.
            setErrorMessage(error.response?.data?.message || "Error resetting password.");
            setDialogOpen(true);
        }
    };

    if (resetSuccess) {
        alert("Successfully changed the password");
        navigate("/login");
    }

    const handleBack = () => {
        if (step === 1) navigate("/login");
        else {
            setStep(step - 1);
            if (step === 2) setOtpSent(false);
            else if (step === 3) setOtpVerified(false);
        }
    };

    const handleDialogClose = () => setDialogOpen(false);

    return (
        <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", backgroundColor: "#0f0c29" }}>
            <style>
                {`
                @keyframes meshGradient {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                @keyframes floatCard {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    50% { transform: translateY(-10px) rotate(0.5deg); }
                }
                @keyframes contentEntrance {
                    from { opacity: 0; transform: scale(0.9) translateY(20px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
                .mesh-bg {
                    background: linear-gradient(-45deg, #240b36, #4f378b, #0f0c29, #2b1055);
                    background-size: 400% 400%;
                    animation: meshGradient 12s ease infinite;
                }
                .glass-panel {
                    background: rgba(255, 255, 255, 0.03);
                    backdrop-filter: blur(15px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 30px;
                    padding: 40px;
                    animation: floatCard 6s ease-in-out infinite;
                }
                .entrance-delay {
                    animation: contentEntrance 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
                }
                `}
            </style>

            <Box className="mesh-bg" sx={{ flex: 1.2, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                <Box className="entrance-delay" sx={{ textAlign: 'center', zIndex: 2, p: 4 }}>
                    <Box component="img" src={logo} sx={{ width: 180, mb: 3, filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.3))' }} />
                    <Typography variant="h1" sx={{ fontFamily: 'Righteous', fontSize: '3.5rem', color: '#fff', mb: 2 }}>
                        Resourcing-Bot
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 300 }}>
                        Secure Password Recovery
                    </Typography>
                </Box>
                <Box sx={{ position: 'absolute', width: '300px', height: '300px', background: 'rgba(79, 55, 139, 0.4)', borderRadius: '50%', filter: 'blur(80px)', top: '10%', left: '10%' }} />
                <Box sx={{ position: 'absolute', width: '400px', height: '400px', background: 'rgba(36, 11, 54, 0.6)', borderRadius: '50%', filter: 'blur(100px)', bottom: '10%', right: '10%' }} />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, background: '#fdfdfd' }}>
                <Box className="glass-panel entrance-delay" sx={{ width: '100%', maxWidth: 480, background: '#fff !important', boxShadow: '0 20px 60px rgba(0,0,0,0.1) !important', position: 'relative' }}>
                    <IconButton onClick={handleBack} sx={{ position: "absolute", top: 15, left: 15, color: "#2b1055" }}>
                        <ArrowBackIcon />
                    </IconButton>

                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Righteous', color: '#2b1055', mb: 1, mt: 3 }}>
                        {step === 3 ? "New Password" : "Forgot Password"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                        {step === 1 && "Enter your email to receive a verification code."}
                        {step === 2 && "Enter the OTP sent to your registered email."}
                        {step === 3 && "Please set your new secure password."}
                    </Typography>

                    <form onSubmit={handleResetPassword}>
                        {step === 1 && (
                            <TextField
                                label="Email Address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                                fullWidth
                                variant="filled"
                                required
                                sx={{ '& .MuiFilledInput-root': { borderRadius: '12px', backgroundColor: '#f0f0f7' } }}
                            />
                        )}

                        {step === 2 && (
                            <TextField
                                label="OTP Code"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                fullWidth
                                variant="filled"
                                required
                                sx={{ '& .MuiFilledInput-root': { borderRadius: '12px', backgroundColor: '#f0f0f7' } }}
                            />
                        )}

                        {step === 3 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="New Password"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    fullWidth
                                    variant="filled"
                                    required
                                    sx={{ '& .MuiFilledInput-root': { borderRadius: '12px', backgroundColor: '#f0f0f7' } }}
                                />
                                <TextField
                                    label="Confirm New Password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    fullWidth
                                    variant="filled"
                                    required
                                    sx={{ '& .MuiFilledInput-root': { borderRadius: '12px', backgroundColor: '#f0f0f7' } }}
                                />
                            </Box>
                        )}

                        <Box sx={{ mt: 4 }}>
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                            ) : (
                                <>
                                    {step === 1 && (
                                        <Button
                                            onClick={handleSendOTP}
                                            fullWidth
                                            variant="contained"
                                            disabled={!email}
                                            sx={{ py: 1.5, borderRadius: '12px', background: 'linear-gradient(90deg, #4f378b, #2b1055)', fontWeight: 700, textTransform: 'none' }}
                                        >
                                            Send OTP
                                        </Button>
                                    )}
                                    {step === 2 && (
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button
                                                onClick={handleSendOTP}
                                                fullWidth
                                                variant="outlined"
                                                sx={{ borderRadius: '12px', color: '#4f378b', borderColor: '#4f378b', textTransform: 'none' }}
                                            >
                                                Resend
                                            </Button>
                                            <Button
                                                onClick={handleVerifyOTP}
                                                fullWidth
                                                variant="contained"
                                                disabled={!otp}
                                                sx={{ borderRadius: '12px', background: 'linear-gradient(90deg, #4f378b, #2b1055)', textTransform: 'none' }}
                                            >
                                                Verify
                                            </Button>
                                        </Box>
                                    )}
                                    {step === 3 && (
                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            sx={{ py: 1.5, borderRadius: '12px', background: 'linear-gradient(90deg, #4f378b, #2b1055)', fontWeight: 700, textTransform: 'none' }}
                                        >
                                            Reset Password
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>
                    </form>
                </Box>
            </Box>

            <Dialog open={dialogOpen} onClose={handleDialogClose} PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>{errorMessage}{responseMessage}</Typography>
                    <Button onClick={handleDialogClose} fullWidth variant="contained" sx={{ bgcolor: '#4f378b' }}>Close</Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ResetPasswordForm;